import { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import AnalyticsCard from './components/AnalyticsCard';
import CustomerScanTable from './components/CustomerScanTable';
import PostCreator from './components/PostCreator';
import QRCodeManager from './components/QRCodeManager';
import EngagementChart from './components/EngagementChart';
import ProfileManager from './components/ProfileManager';
import NotificationSettings from './components/NotificationSettings';
import { useAuth } from '../../contexts/AuthContext';
import { businessService } from '../../services/businessService';
import { qrScanService } from '../../services/qrScanService';
import { useNavigate } from 'react-router-dom';

const BusinessDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [businessProfile, setBusinessProfile] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/business-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadBusinessData();
    }
  }, [user]);

  const loadBusinessData = async () => {
    try {
      setLoading(true);
      const [business, businessScans] = await Promise.all([
        businessService?.getBusinessByUserId(user?.id),
        businessService?.getBusinessByUserId(user?.id)?.then(b => 
          b ? qrScanService?.getBusinessScans(b?.id, 10) : []
        )
      ]);
      
      setBusinessProfile(business);
      setScans(businessScans);
    } catch (error) {
      console.error('Error loading business data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!businessProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Icon name="AlertCircle" size={48} className="text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Perfil Empresarial No Encontrado</h2>
          <p className="text-muted-foreground mb-6">
            No se encontró un perfil empresarial asociado a tu cuenta.
          </p>
          <button
            onClick={() => navigate('/business-registration')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Crear Perfil Empresarial
          </button>
        </div>
      </div>
    );
  }

  const mockBusinessUser = {
    type: 'business',
    notifications: {
      dashboard: 3
    }
  };

  const mockAnalytics = {
    totalScans: businessProfile?.totalScans || 0,
    scanChange: "+18.2%",
    scanChangeType: "positive",
    uniqueCustomers: businessProfile?.uniqueCustomers || 0,
    customerChange: "+12.5%",
    customerChangeType: "positive",
    avgScansPerDay: Math.round((businessProfile?.totalScans || 0) / 30),
    avgChange: "+5.3%",
    avgChangeType: "positive",
    conversionRate: 68,
    conversionChange: "+3.1%",
    conversionChangeType: "positive"
  };

  const mockWeeklyData = [
    { day: "Lun", scans: 45, visits: 120 },
    { day: "Mar", scans: 52, visits: 135 },
    { day: "Mié", scans: 38, visits: 98 },
    { day: "Jue", scans: 61, visits: 156 },
    { day: "Vie", scans: 73, visits: 189 },
    { day: "Sáb", scans: 89, visits: 234 },
    { day: "Dom", scans: 34, visits: 87 }
  ];

  const mockBusinessData = {
    name: businessProfile?.name || 'Mi Negocio',
    description: businessProfile?.description || '',
    address: businessProfile?.address || '',
    phone: businessProfile?.phone || '',
    email: businessProfile?.email || '',
    website: businessProfile?.website || '',
    qrCode: businessProfile?.qrCode || ''
  };

  const mockScans = scans || [];

  const mockNotificationSettings = {
    emailNotifications: true,
    smsNotifications: false,
    scanAlerts: true,
    weeklyReports: true,
    monthlyReports: true
  };

  const handlePublishPost = (postData) => {
    console.log('Publishing post:', postData);
  };

  const handleUpdateProfile = (updatedData) => {
    console.log('Updating profile:', updatedData);
  };

  const handleSaveNotifications = (settings) => {
    console.log('Saving notification settings:', settings);
  };

  const tabs = [
  { id: 'overview', label: 'Resumen', icon: 'LayoutDashboard' },
  { id: 'scans', label: 'Escaneos', icon: 'QrCode' },
  { id: 'posts', label: 'Publicaciones', icon: 'FileText' },
  { id: 'qr', label: 'Mi Código QR', icon: 'Smartphone' },
  { id: 'profile', label: 'Perfil', icon: 'Settings' },
  { id: 'notifications', label: 'Notificaciones', icon: 'Bell' }];


  return (
    <div className="min-h-screen bg-background">
      <Header user={mockBusinessUser} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Panel Empresarial</h1>
            <p className="text-muted-foreground">
              Gestiona tu negocio, interactúa con clientes y analiza tu rendimiento
            </p>
          </div>

          <div className="mb-6 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {tabs?.map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground shadow-sm' :
                'bg-card text-foreground hover:bg-muted border border-border'}`
                }>

                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              )}
            </div>
          </div>

          {activeTab === 'overview' &&
          <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <AnalyticsCard
                icon="QrCode"
                title="Total Escaneos"
                value={mockAnalytics?.totalScans?.toLocaleString('es-CO')}
                change={mockAnalytics?.scanChange}
                changeType={mockAnalytics?.scanChangeType} />

                <AnalyticsCard
                icon="Users"
                title="Clientes Únicos"
                value={mockAnalytics?.uniqueCustomers?.toLocaleString('es-CO')}
                change={mockAnalytics?.customerChange}
                changeType={mockAnalytics?.customerChangeType} />

                <AnalyticsCard
                icon="TrendingUp"
                title="Promedio Diario"
                value={mockAnalytics?.avgScansPerDay}
                change={mockAnalytics?.avgChange}
                changeType={mockAnalytics?.avgChangeType} />

                <AnalyticsCard
                icon="Target"
                title="Tasa de Conversión"
                value={`${mockAnalytics?.conversionRate}%`}
                change={mockAnalytics?.conversionChange}
                changeType={mockAnalytics?.conversionChangeType} />

              </div>

              <EngagementChart data={mockWeeklyData} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <QRCodeManager businessData={mockBusinessData} />
                <div className="bg-card rounded-lg border border-border shadow-card p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Acciones Rápidas</h3>
                  <div className="space-y-3">
                    <button
                    onClick={() => setActiveTab('posts')}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/20 transition-colors">

                      <div className="flex items-center gap-3">
                        <Icon name="FileText" size={20} color="var(--color-primary)" />
                        <span className="text-sm font-medium text-foreground">Crear Publicación</span>
                      </div>
                      <Icon name="ChevronRight" size={20} />
                    </button>
                    <button
                    onClick={() => setActiveTab('scans')}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-secondary/5 hover:bg-secondary/10 border border-secondary/20 transition-colors">

                      <div className="flex items-center gap-3">
                        <Icon name="Users" size={20} color="var(--color-secondary)" />
                        <span className="text-sm font-medium text-foreground">Ver Clientes</span>
                      </div>
                      <Icon name="ChevronRight" size={20} />
                    </button>
                    <button
                    onClick={() => setActiveTab('profile')}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-accent/5 hover:bg-accent/10 border border-accent/20 transition-colors">

                      <div className="flex items-center gap-3">
                        <Icon name="Settings" size={20} color="var(--color-accent)" />
                        <span className="text-sm font-medium text-foreground">Actualizar Perfil</span>
                      </div>
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          }

          {activeTab === 'scans' &&
          <CustomerScanTable scans={mockScans} />
          }

          {activeTab === 'posts' &&
          <PostCreator onPublish={handlePublishPost} />
          }

          {activeTab === 'qr' &&
          <div className="max-w-2xl mx-auto">
              <QRCodeManager businessData={mockBusinessData} />
            </div>
          }

          {activeTab === 'profile' &&
          <ProfileManager
            businessData={mockBusinessData}
            onUpdate={handleUpdateProfile} />

          }

          {activeTab === 'notifications' &&
          <NotificationSettings
            settings={mockNotificationSettings}
            onSave={handleSaveNotifications} />

          }
        </div>
      </main>
    </div>);

};

export default BusinessDashboard;